// Produce a cropped (and optionally rotated / flipped / filtered) image as a data URL.
function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e) => reject(e));
    image.src = url;
  });
}

const toRad = (deg) => (deg * Math.PI) / 180;

function rotatedSize(width, height, rotation) {
  const r = toRad(rotation);
  return {
    width: Math.abs(Math.cos(r) * width) + Math.abs(Math.sin(r) * height),
    height: Math.abs(Math.sin(r) * width) + Math.abs(Math.cos(r) * height),
  };
}

// Build a CSS filter string from adjustment values (100 = neutral for the first three).
export function buildFilter({ brightness = 100, contrast = 100, saturate = 100, grayscale = 0, sepia = 0, blur = 0 } = {}) {
  const parts = [];
  if (brightness !== 100) parts.push(`brightness(${brightness}%)`);
  if (contrast !== 100) parts.push(`contrast(${contrast}%)`);
  if (saturate !== 100) parts.push(`saturate(${saturate}%)`);
  if (grayscale) parts.push(`grayscale(${grayscale}%)`);
  if (sepia) parts.push(`sepia(${sepia}%)`);
  if (blur) parts.push(`blur(${blur}px)`);
  return parts.join(' ');
}

// opts: { flipX, flipY, filter (CSS filter string), maxSize (longest edge in px) }
export async function getCroppedImg(imageSrc, pixelCrop, rotation = 0, opts = {}) {
  const { flipX = false, flipY = false, filter = '', maxSize = 0 } = opts;
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const { width: bW, height: bH } = rotatedSize(image.width, image.height, rotation);
  canvas.width = Math.max(1, Math.round(bW));
  canvas.height = Math.max(1, Math.round(bH));

  if (filter) ctx.filter = filter;
  ctx.translate(bW / 2, bH / 2);
  ctx.rotate(toRad(rotation));
  ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);
  ctx.drawImage(image, 0, 0);
  ctx.filter = 'none';

  const cx = Math.max(0, Math.round(pixelCrop.x));
  const cy = Math.max(0, Math.round(pixelCrop.y));
  const cw = Math.max(1, Math.min(Math.round(pixelCrop.width), canvas.width - cx));
  const ch = Math.max(1, Math.min(Math.round(pixelCrop.height), canvas.height - cy));
  const data = ctx.getImageData(cx, cy, cw, ch);

  // Keep transparency (PNG) when the source has any non-opaque pixel; otherwise JPEG.
  const px = data.data;
  let transparent = false;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] < 255) { transparent = true; break; }
  }

  const out = document.createElement('canvas');
  out.width = cw; out.height = ch;
  out.getContext('2d').putImageData(data, 0, 0);

  let result = out;
  const longest = Math.max(cw, ch);
  if (maxSize && longest > maxSize) {
    const scale = maxSize / longest;
    const small = document.createElement('canvas');
    small.width = Math.max(1, Math.round(cw * scale));
    small.height = Math.max(1, Math.round(ch * scale));
    const sctx = small.getContext('2d');
    sctx.imageSmoothingEnabled = true;
    sctx.imageSmoothingQuality = 'high';
    sctx.drawImage(out, 0, 0, small.width, small.height);
    result = small;
  }

  return transparent ? result.toDataURL('image/png') : result.toDataURL('image/jpeg', 0.9);
}
